import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { toast } from 'sonner';
import type { Tables } from '@/integrations/supabase/types';

const teamSchema = z.object({
  name: z.string().trim().min(2, 'Nome deve ter pelo menos 2 caracteres').max(100),
  icon: z.string().trim().min(1).max(10).default('👥'),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  joinAsAdmin: z.boolean().default(true),
});

type TeamFormValues = z.infer<typeof teamSchema>;

interface TeamFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  team?: Tables<'teams'>;
}

export const TeamFormModal = ({ open, onOpenChange, team }: TeamFormModalProps) => {
  const { user } = useAuth();
  const { isAdmin } = useProfile();

  const queryClient = useQueryClient();
  const isEditing = !!team;

  const form = useForm<TeamFormValues>({
    resolver: zodResolver(teamSchema),
    defaultValues: { name: '', icon: '👥', description: '', joinAsAdmin: true },
  });

  useEffect(() => {
    if (open) {
      if (team) {
        form.reset({ name: team.name, icon: team.icon, description: team.description ?? '', joinAsAdmin: true });
      } else {
        form.reset({ name: '', icon: '👥', description: '', joinAsAdmin: !isAdmin ? true : true });
      }
    }
  }, [open, team, isAdmin]);

  const createTeam = useMutation({
    mutationFn: async (values: TeamFormValues) => {
      // Create team
      const { data: newTeam, error } = await supabase
        .from('teams')
        .insert({ name: values.name, icon: values.icon, description: values.description || null })
        .select()
        .single();
      if (error) throw error;

      // Add creator as admin member if requested (default true).
      // Platform admins can opt out via checkbox to avoid polluting their personal sidebar.
      if (values.joinAsAdmin) {
        const { error: memberError } = await supabase
          .from('team_members')
          .insert({ team_id: newTeam.id, user_id: user!.id, role: 'admin' });
        if (memberError) throw memberError;
      }

      return newTeam;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success('Time criado com sucesso');
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(`Erro ao criar time: ${err.message}`);
    },
  });

  const updateTeam = useMutation({
    mutationFn: async (values: TeamFormValues) => {
      const { error } = await supabase
        .from('teams')
        .update({ name: values.name, icon: values.icon, description: values.description || null })
        .eq('id', team!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success('Time atualizado');
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(`Erro ao atualizar time: ${err.message}`);
    },
  });

  const onSubmit = (values: TeamFormValues) => {
    if (isEditing) {
      updateTeam.mutate(values);
    } else {
      createTeam.mutate(values);
    }
  };

  const isPending = createTeam.isPending || updateTeam.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Time' : 'Novo Time'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Nome + Ícone */}
          <div className="flex gap-3">
            <div className="w-16">
              <label className="block text-sm font-medium text-primary mb-1.5">Ícone</label>
              <input
                {...form.register('icon')}
                className="w-full px-2 py-2 rounded-md border border-color bg-surface text-center text-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-primary mb-1.5">Nome *</label>
              <input
                {...form.register('name')}
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Nome do time"
              />
              {form.formState.errors.name && (
                <p className="text-xs text-destructive mt-1">{form.formState.errors.name.message}</p>
              )}
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-primary mb-1.5">Descrição</label>
            <textarea
              {...form.register('description')}
              rows={3}
              className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
              placeholder="Descrição opcional do time"
            />
          </div>

          {/* Participar do time como admin (apenas na criação) */}
          {!isEditing && (
            <label className="flex items-start gap-2.5 p-3 rounded-md border border-color bg-surface cursor-pointer hover:bg-hover transition-colors">
              <input
                type="checkbox"
                {...form.register('joinAsAdmin')}
                className="mt-0.5 h-4 w-4 rounded border-color text-brand-primary focus:ring-brand-primary cursor-pointer"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-primary">Participar do time como admin</div>
                <div className="text-xs text-secondary mt-0.5">
                  Você será adicionado como administrador. Desmarque para criar um time sem se incluir nele.
                </div>
              </div>
            </label>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 border border-color rounded-md text-sm font-medium text-primary hover:bg-hover"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 brand-primary rounded-md text-sm font-medium disabled:opacity-50"
            >
              {isPending ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar Time'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
